import React, { useRef, useState, useCallback } from "react";
import {
    intl,
    DmsAltBox,
    DmsPage,
    DmsTable,
    DmsButton,
    DmsPermissions,
    DmsFlexContainer,
    DmsButtonGroup,
    fetchData,
} from "dmsbasereact";
import Swal from "sweetalert2";
import { Typography, Box, Chip, IconButton, Tooltip } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import GroupIcon from "@mui/icons-material/Group";
import { Permissions } from "../../constants/Permissions";
import { DirectorGroupFormModal } from "../../components/DirectorGroupFormModal";

export const DirectorGroupsPage = () => {
    const table = useRef(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedGroup, setSelectedGroup] = useState(null);

    const handleOpenCreate = useCallback(() => {
        setSelectedGroup(null);
        setModalOpen(true);
    }, []);

    const handleOpenEdit = useCallback((group) => {
        setSelectedGroup(group);
        setModalOpen(true);
    }, []);

    const handleCloseModal = useCallback(() => {
        setModalOpen(false);
        setSelectedGroup(null);
    }, []);

    const handleSave = useCallback(() => {
        if (table.current) {
            table.current.updateTable();
        }
    }, []);

    const handleDelete = useCallback(async (group) => {
        const result = await Swal.fire({
            icon: "warning",
            title: intl.get("ConfirmDelete").d("Confirm Delete"),
            text: intl.get("ConfirmDeleteGroup").d("Are you sure you want to delete this group?"),
            showCancelButton: true,
            confirmButtonText: intl.get("Yes").d("Yes"),
            cancelButtonText: intl.get("No").d("No"),
        });

        if (result.isConfirmed) {
            try {
                await fetchData(`/director_group/${group.pk}`, "DELETE", true);
                Swal.fire({
                    icon: "success",
                    title: intl.get("Success").d("Success"),
                    text: intl.get("GroupDeletedSuccessfully").d("Group deleted successfully"),
                });
                if (table.current) {
                    table.current.updateTable();
                }
            } catch (error) {
                console.error("Failed to delete group:", error);
                Swal.fire({
                    icon: "error",
                    title: intl.get("Error").d("Error"),
                    text: intl.get("FailedToDeleteGroup").d("Failed to delete group"),
                });
            }
        }
    }, []);

    const header = [
        {
            label: intl.get("Name").d("Name"),
            column_name: "name",
            filter: true,
            element: (row) => (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <GroupIcon color="primary" />
                    <Typography fontWeight="500">{row.name}</Typography>
                </Box>
            ),
        },
        {
            label: intl.get("Description").d("Description"),
            column_name: "description",
            filter: true,
            element: (row) => row.description || "-",
        },
        {
            label: intl.get("Members").d("Members"),
            column_name: "members",
            sortable: false,
            filter: false,
            element: (row) => (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {row.members && row.members.length > 0 ? (
                        row.members.map((member, index) => (
                            <Chip
                                key={index}
                                label={member.dms_user?.user_name || member.dms_user_pk}
                                size="small"
                                variant="outlined"
                            />
                        ))
                    ) : (
                        <Typography color="text.secondary" variant="body2">
                            {intl.get("NoMembers").d("No members")}
                        </Typography>
                    )}
                </Box>
            ),
        },
        {
            label: intl.get("Actions").d("Actions"),
            column_name: "actions",
            width: "120px",
            sortable: false,
            filter: false,
            element: (row) => (
                <DmsPermissions permissions={[Permissions.DIRECTOR_GROUPS_MANAGE]}>
                    <DmsButtonGroup>
                        <Tooltip title={intl.get("Edit").d("Edit")}>
                            <IconButton
                                size="small"
                                onClick={() => handleOpenEdit(row)}
                                color="primary"
                            >
                                <EditIcon />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title={intl.get("Delete").d("Delete")}>
                            <IconButton
                                size="small"
                                onClick={() => handleDelete(row)}
                                color="error"
                            >
                                <DeleteIcon />
                            </IconButton>
                        </Tooltip>
                    </DmsButtonGroup>
                </DmsPermissions>
            ),
        },
    ];

    const toolbar = (
        <DmsFlexContainer>
            <DmsFlexContainer.Left>
                <DmsPermissions permissions={[Permissions.DIRECTOR_GROUPS_MANAGE]}>
                    <DmsButton
                        icon={<AddIcon />}
                        text={intl.get("CreateDirectorGroup").d("Create Director Group")}
                        onClick={handleOpenCreate}
                        variant="primary"
                    />
                </DmsPermissions>
            </DmsFlexContainer.Left>
        </DmsFlexContainer>
    );

    return (
        <DmsPage name="DirectorGroups">
            <DmsAltBox.Card>
                <DmsAltBox.Header>
                    <Typography variant="h3" sx={{ fontSize: "1.1rem" }}>
                        {intl.get("DirectorGroups").d("Director Groups")}
                    </Typography>
                </DmsAltBox.Header>
                <DmsAltBox.Body>
                    <DmsTable
                        header={header}
                        managed={true}
                        needs_authentication={true}
                        ref={table}
                        url="/director_group/table"
                        toolbar={toolbar}
                        export_table={false}
                    />
                </DmsAltBox.Body>
            </DmsAltBox.Card>

            <DirectorGroupFormModal
                isVisible={modalOpen}
                onClose={handleCloseModal}
                onSave={handleSave}
                group={selectedGroup}
            />
        </DmsPage>
    );
};

export default DirectorGroupsPage;
